import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analysisApi } from '@/services/analysisApi';
import { chroApi } from '@/services/api';
import DemoExplainer from '@/components/DemoExplainer';
import InlineMessage from '@/components/InlineMessage';
import HintBar from '@/components/HintBar';

const HINTS = [
  'Upload JD PDFs or type role titles directly',
  'Add at least 3 roles to enable analysis',
  "We'll map each title to an O*NET occupational category",
  'Titles are matched using Claude AI semantic fallback',
];

// ── Design tokens (dark theme — values resolve via tokens.css) ────────
// teal/gold kept as hex so `${T.teal}XX` alpha concatenations stay valid.
const T = {
  bg: 'var(--bg)',
  text: 'var(--text)',
  textMuted: 'var(--t3)',
  teal: '#00D4C8',
  gold: '#C9A84C',
  cardBg: 'rgba(255,255,255,0.025)',
  cardBorder: 'rgba(255,255,255,0.07)',
  cardShadow: 'none',
  radius: '12px',
};

const STEPS = ['Upload', 'Discipline', 'Analysis', 'Blueprint'];

const FUNCTIONS = [
  'Finance', 'Technology', 'Operations', 'HR', 'Sales',
  'Marketing', 'Legal', 'Customer Service', 'Supply Chain',
  'Research & Development', 'Risk & Compliance', 'Data & Analytics',
];

// OCF (O*NET) title → business function. Used to auto-infer the function
// from the confirmed roles instead of asking the user to pick one.
const OCF_TO_FUNCTION = {
  // Healthcare
  'Medical and Health Services Managers': 'Clinical Operations',
  'Registered Nurses': 'Clinical Operations',
  'Medical Scientists, Except Epidemiologists': 'Clinical Operations',
  'Health Information Technologists and Medical Registrars': 'Clinical Operations',
  'Medical Records Specialists': 'Clinical Operations',
  'Receptionists and Information Clerks': 'Patient Services',

  // Finance
  'Financial Managers': 'Finance',
  'Financial Analysts': 'Finance',
  'Bill and Account Collectors': 'Finance',
  'Accountants and Auditors': 'Finance',
  'Credit Analysts': 'Finance',
  'Loan Officers': 'Finance',
  'Compliance Officers': 'Finance',

  // HR
  'Human Resources Managers': 'HR & People',
  'Human Resources Specialists': 'HR & People',
  'Training and Development Managers': 'HR & People',

  // Technology
  'Software Developers': 'Technology',
  'Computer and Information Systems Managers': 'Technology',
  'Data Scientists': 'Technology',
  'Information Security Analysts': 'Technology',
  'Computer Systems Analysts': 'Technology',

  // Operations / General
  'General and Operations Managers': 'Operations',
  'Management Analysts': 'Operations',
  'Project Management Specialists': 'Operations',
  'Logisticians': 'Operations',
  'Transportation, Storage, and Distribution Managers': 'Operations',
  'Purchasing Managers': 'Operations',
  'First-Line Supervisors of Production and Operating Workers': 'Operations',

  // Marketing / Content
  'Marketing Managers': 'Marketing',
  'Advertising Sales Agents': 'Marketing',
  'Editors': 'Marketing',
  'Writers and Authors': 'Marketing',
  'Producers and Directors': 'Marketing',

  // Sales
  'Sales Managers': 'Sales',
  'Securities, Commodities, and Financial Services Sales Agents': 'Sales',

  // Supply Chain / Manufacturing
  'Quality Control Inspectors': 'Manufacturing',
  'Industrial Engineers': 'Manufacturing',
  'Occupational Health and Safety Specialists': 'Manufacturing',
  'Freight and Cargo Agents': 'Supply Chain',
  'Couriers and Messengers': 'Operations',
};

function inferFunction(confirmedRoles) {
  const votes = {};
  for (const role of confirmedRoles) {
    const ocf = role.ocf_title || role.matched_title || role.onet_title || '';
    const fn = OCF_TO_FUNCTION[ocf];
    if (fn) votes[fn] = (votes[fn] || 0) + 1;
  }
  if (Object.keys(votes).length === 0) return 'Operations';
  return Object.entries(votes).sort((a, b) => b[1] - a[1])[0][0];
}

// ── Reusable micro-components ─────────────────────────

const STATUS_STEP = {
  roles_confirmed: 1,
  discipline_selected: 2,
  molecularised: 3,
  blueprint_generated: 4,
};
const STEP_ROUTES = ['/analysis', '/analysis/discipline', '/analysis/results', '/blueprint'];

const StepIndicator = ({ current = 0, completedStep = 0, onNavigate }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
    <span style={{ fontSize: 13, color: T.textMuted, fontFamily: 'Inter, sans-serif' }}>
      Step {current + 1} of {STEPS.length}
    </span>
    <div style={{ display: 'flex', gap: 4 }}>
      {STEPS.map((s, i) => {
        const done = i < completedStep;
        const active = i === current;
        const clickable = done && onNavigate;
        return (
          <div
            key={s}
            onClick={() => clickable && onNavigate(STEP_ROUTES[i])}
            style={{
              width: 32, height: 4, borderRadius: 2, cursor: clickable ? 'pointer' : 'default',
              background: i <= current ? T.teal : done ? `${T.teal}60` : T.cardBorder,
              transition: 'background 0.3s',
            }}
          />
        );
      })}
    </div>
    <div style={{ display: 'flex', gap: 2, fontSize: 13, fontFamily: 'Inter, sans-serif' }}>
      {STEPS.map((s, i) => {
        const done = i < completedStep;
        const active = i === current;
        const clickable = done && onNavigate;
        return (
          <span key={s} onClick={() => clickable && onNavigate(STEP_ROUTES[i])}
            style={{ color: active ? T.text : done ? T.teal : T.textMuted, fontWeight: active ? 600 : 400, cursor: clickable ? 'pointer' : 'default' }}>
            {i > 0 ? ' \u2192 ' : ''}{done ? '\u2713 ' : ''}{s}
          </span>
        );
      })}
    </div>
  </div>
);

const ConfidenceBadge = ({ value }) => {
  const color = value >= 0.8 ? '#16a34a' : value >= 0.6 ? T.gold : '#dc2626';
  const label = value >= 0.8 ? 'High' : value >= 0.6 ? 'Medium' : 'Low';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 99,
        background: `${color}18`,
        color,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {label} confidence
    </span>
  );
};

// ── Main component ────────────────────────────────────

const Analysis = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // State
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'titles'
  // Kept for session restoration + lookupTitles filter; user no longer
  // picks this — handleContinue infers it from confirmed roles via
  // inferFunction(). Initialise empty so canContinue isn't gated on it.
  const [selectedFunction, setSelectedFunction] = useState('');

  // Inline messages
  const [inlineMsg, setInlineMsg] = useState(null);

  // Upload mode
  const [uploadedFiles, setUploadedFiles] = useState([]); // { filename, extracted_title, responsibilities, confidence, confirmed, editingTitle }
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Title mode
  const [titleInput, setTitleInput] = useState('');
  const [titleEntries, setTitleEntries] = useState([]); // { title, onet_code, onet_title, confirmed, matches?, choosing? }
  const [lookingUp, setLookingUp] = useState(false);

  // Combined confirmed roles
  const [saving, setSaving] = useState(false);

  // Session resume
  const [savedSession, setSavedSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);

  // Org-scoped function list (CHRO users) - null = not yet loaded, [] = loaded empty, [...] = loaded
  const [orgFunctions, setOrgFunctions] = useState(null);
  const isDemoUser = typeof window !== 'undefined' && localStorage.getItem('is_demo') === 'true';

  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  useEffect(() => {
    if (isDemoUser) return;
    (async () => {
      try {
        const res = await chroApi.getSignalRoom();
        const fns = res?.data?.functions || [];
        const names = fns.map((f) => f.name).filter(Boolean);
        setOrgFunctions(names);
        if (names.length === 1) {
          setSelectedFunction(names[0]);
        }
      } catch (e) {
        // Leave as null → fall back to hardcoded FUNCTIONS
      }
    })();
  }, [isDemoUser]);
  useEffect(() => {
    (async () => {
      try {
        let session = null;
        if (localStorage.getItem('is_demo') === 'true') {
          localStorage.removeItem('reai_session_id');
          localStorage.removeItem('analysis_session_id');
        }
        // Check localStorage first
        const storedId = localStorage.getItem('reai_session_id');
        if (storedId) {
          const res = await analysisApi.getSession(storedId).catch(() => null);
          if (res?.data?.session) session = res.data.session;
        }
        // Fallback: check for latest session in DB
        if (!session) {
          const res = await analysisApi.getLatestSession().catch(() => null);
          if (res?.data?.session) {
            session = res.data.session;
            localStorage.setItem('reai_session_id', session.id);
          }
        }
        if (session) {
          setSavedSession(session);
          // Restore roles into the UI
          _restoreSessionRoles(session);
        }
      } catch {
        // No saved session, that's fine
      } finally {
        setLoadingSession(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const _restoreSessionRoles = (session) => {
    if (!session?.roles?.length) return;
    // Set function
    if (session.function) setSelectedFunction(session.function);
    // Restore localStorage keys
    localStorage.setItem('analysis_session_id', session.id);
    localStorage.setItem('analysis_function', session.function || '');
    localStorage.setItem('analysis_roles', JSON.stringify(session.roles));
    if (session.value_discipline) {
      localStorage.setItem('analysis_discipline', session.value_discipline);
    }
    if (session.molecularisation_results) {
      localStorage.setItem('analysis_mol_results', JSON.stringify({
        session_id: session.id,
        value_discipline: session.value_discipline,
        roles: session.molecularisation_results,
      }));
    }
    // Split roles by source and restore to correct state arrays
    const jdRoles = session.roles.filter((r) => r.source === 'jd_upload');
    const titleRoles = session.roles.filter((r) => r.source !== 'jd_upload');

    if (jdRoles.length > 0) {
      setUploadedFiles(jdRoles.map((r) => ({
        filename: r.original_title || r.title,
        extracted_title: r.original_title || r.title,
        responsibilities: [],
        confidence: 0.9,
        confirmed: true,
        editingTitle: false,
        onet_code: r.onet_code || '',
        filled: r.filled !== false,
        task_scores: r.task_scores || null,
        source: 'jd_upload',
      })));
    }

    if (titleRoles.length > 0) {
      setTitleEntries(titleRoles.map((r) => ({
        title: r.original_title || r.title,
        onet_code: r.onet_code || '',
        onet_title: r.title,
        confirmed: true,
        choosing: false,
        matches: [],
        isFallback: false,
        filled: r.filled !== false,
        source: 'title_lookup',
      })));
    }

    // Set tab based on majority source
    const jdCount = jdRoles.length;
    const titleCount = titleRoles.length;
    setActiveTab(jdCount >= titleCount && jdCount > 0 ? 'upload' : 'titles');
  };

  const confirmedRoles = [
    ...uploadedFiles.filter((f) => f.confirmed).map((f) => ({
      title: f.extracted_title,
      original_title: f.extracted_title,
      onet_code: f.onet_code || '',
      source: 'jd_upload',
      filled: f.filled !== false,
      task_scores: f.task_scores || null,
    })),
    ...titleEntries.filter((t) => t.confirmed).map((t) => ({
      title: t.onet_title || t.title,
      original_title: t.title,
      onet_code: t.onet_code,
      source: 'title_lookup',
      filled: t.filled !== false,
    })),
  ];

  const roleCount = confirmedRoles.length;
  const minRoles = 1;
  const maxRoles = 10;
  const canContinue = roleCount >= minRoles;

  // ── Upload handlers ──────────────────────────────────

  const handleFiles = useCallback(
    async (files) => {
      console.log('[Upload] files received:', files?.length, Array.from(files || []).map(f => f.name));
      const allowed = ['.pdf', '.docx', '.txt'];
      const valid = Array.from(files || []).filter((f) => {
        const ext = '.' + f.name.split('.').pop().toLowerCase();
        const ok = allowed.includes(ext);
        if (!ok) console.log('[Upload] skipping unsupported file:', f.name, ext);
        return ok;
      });

      // Check file sizes (5MB max)
      const oversized = Array.from(files || []).find((f) => f.size > 5 * 1024 * 1024);
      if (oversized) {
        setInlineMsg({ type: 'warning', title: `${oversized.name} is too large (${(oversized.size / 1024 / 1024).toFixed(1)} MB)`, message: 'Maximum file size is 5MB. This JD may contain embedded images.', suggestions: ['Try saving as a plain text file', 'Or copy the text into a new Word document'] });
        return;
      }

      if (valid.length === 0) {
        const rejected = Array.from(files || []).map((f) => f.name).join(', ');
        setInlineMsg({ type: 'error', title: `${rejected} is not supported`, message: 'We accept PDF, Word (.docx), and text (.txt) files only.', suggestions: ['Most JDs can be saved as PDF directly from Word or your ATS'] });
        return;
      }

      const totalAfter = uploadedFiles.length + valid.length;
      if (totalAfter > maxRoles) {
        setInlineMsg({ type: 'warning', title: `Maximum ${maxRoles} files per analysis`, message: `You've added ${uploadedFiles.length} files. Remove some to continue, or run a second analysis for the remaining roles.` });
        return;
      }

      setUploading(true);
      try {
        console.log('[Upload] sending', valid.length, 'files to API...');
        const res = await analysisApi.uploadJds(valid);
        console.log('[Upload] API response:', res.data);
        const parsed = (res.data.files || []).map((f) => ({
          ...f,
          confirmed: f.confidence >= 0.5,
          editingTitle: false,
          onet_code: '',
          filled: true,
        }));
        setUploadedFiles((prev) => [...prev, ...parsed]);
      } catch (err) {
        console.error('[Upload] failed:', err);
        setInlineMsg({
          type: 'error',
          title: 'Upload failed',
          message: 'This sometimes happens with scanned PDFs or image-based documents.',
          suggestions: ['Copy the JD text and paste it into a new Word document, then upload', 'Or use the "Enter Titles" tab and type the job titles manually'],
          actions: [
            { label: 'Try Again', onClick: () => { setInlineMsg(null); fileInputRef.current?.click(); } },
            { label: 'Switch to Enter Titles', onClick: () => { setInlineMsg(null); setActiveTab('titles'); } },
          ],
        });
      } finally {
        setUploading(false);
      }
    },
    [uploadedFiles.length]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const confirmUploadedFile = (idx) => {
    setUploadedFiles((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, confirmed: true, editingTitle: false } : f))
    );
  };

  const updateUploadedTitle = (idx, newTitle) => {
    setUploadedFiles((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, extracted_title: newTitle } : f))
    );
  };

  const removeUploadedFile = (idx) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Title lookup handlers ────────────────────────────

  const handleAddTitle = async () => {
    const trimmed = titleInput.trim();
    if (!trimmed) return;

    setLookingUp(true);
    try {
      const res = await analysisApi.lookupTitles([trimmed], selectedFunction);
      const result = res.data.results?.[0];
      if (!result || !result.matches?.length) {
        setTitleEntries((prev) => [
          ...prev,
          { title: trimmed, onet_code: '', onet_title: '', confirmed: false, matches: [], choosing: true, isFallback: false },
        ]);
      } else if (result.matches.length === 1 && result.matches[0].similarity_score > 0.85) {
        const m = result.matches[0];
        setTitleEntries((prev) => [
          ...prev,
          { title: trimmed, onet_code: m.onet_code, onet_title: m.onet_title, confirmed: true, matches: result.matches, choosing: false, isFallback: false },
        ]);
      } else {
        setTitleEntries((prev) => [
          ...prev,
          { title: trimmed, onet_code: '', onet_title: '', confirmed: false, matches: result.matches, choosing: true, isFallback: !!result.is_fallback },
        ]);
      }
      setTitleInput('');
    } catch (err) {
      console.error(err);
      setInlineMsg({ type: 'error', title: 'Title lookup failed', message: 'Could not connect to the classification service. Please try again.', actions: [{ label: 'Try Again', onClick: () => { setInlineMsg(null); handleAddTitle(); } }] });
    } finally {
      setLookingUp(false);
    }
  };

  const selectMatch = (entryIdx, match) => {
    setTitleEntries((prev) =>
      prev.map((t, i) =>
        i === entryIdx
          ? { ...t, onet_code: match.onet_code, onet_title: match.onet_title, confirmed: true, choosing: false }
          : t
      )
    );
  };

  const removeTitleEntry = (idx) => {
    setTitleEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Continue handler ─────────────────────────────────

  const handleContinue = async () => {
    if (roleCount > 0 && roleCount < minRoles) {
      setInlineMsg({ type: 'info', title: `You have ${roleCount} role${roleCount !== 1 ? 's' : ''} - minimum is ${minRoles}`, message: `Add at least ${minRoles - roleCount} more role${minRoles - roleCount !== 1 ? 's' : ''} to get a meaningful transformation analysis. More roles = richer Blueprint output.` });
      return;
    }
    if (!canContinue) return;
    setSaving(true);
    try {
      const inferredFunction = inferFunction(confirmedRoles);
      const res = await analysisApi.confirmRoles(inferredFunction, confirmedRoles);
      const sessionId = res.data.session_id;
      localStorage.setItem('reai_session_id', sessionId);
      localStorage.setItem('analysis_session_id', sessionId);
      localStorage.setItem('analysis_function', inferredFunction);
      localStorage.setItem('analysis_roles', JSON.stringify(confirmedRoles));
      navigate('/analysis/discipline');
    } catch (err) {
      console.error(err);
      setInlineMsg({ type: 'error', title: 'Failed to save roles', message: err.response?.data?.detail || 'Please try again.', actions: [{ label: 'Try Again', onClick: () => { setInlineMsg(null); handleContinue(); } }] });
    } finally {
      setSaving(false);
    }
  };

  // ── Session resume helpers ───────────────────────────

  const resumeTarget = (status) => {
    switch (status) {
      case 'roles_confirmed': return '/analysis/discipline';
      case 'discipline_selected': return '/analysis/results';
      case 'molecularised': return '/analysis/results';
      case 'blueprint_generated': return '/blueprint';
      default: return '/analysis/discipline';
    }
  };

  const handleStartFresh = async () => {
    if (savedSession) {
      try {
        await analysisApi.deleteSession(savedSession.id);
      } catch { /* ignore */ }
    }
    localStorage.removeItem('reai_session_id');
    localStorage.removeItem('analysis_session_id');
    localStorage.removeItem('analysis_function');
    localStorage.removeItem('analysis_roles');
    localStorage.removeItem('analysis_mol_results');
    localStorage.removeItem('analysis_discipline');
    localStorage.removeItem('analysis_blueprint');
    setSavedSession(null);
    setTitleEntries([]);
    setUploadedFiles([]);
    setSelectedFunction('');
  };

  const handleResume = () => {
    if (!savedSession) return;
    // Restore localStorage keys for downstream pages
    localStorage.setItem('reai_session_id', savedSession.id);
    localStorage.setItem('analysis_session_id', savedSession.id);
    localStorage.setItem('analysis_function', savedSession.function || '');
    localStorage.setItem('analysis_roles', JSON.stringify(savedSession.roles || []));
    if (savedSession.value_discipline) {
      localStorage.setItem('analysis_discipline', savedSession.value_discipline);
    }
    if (savedSession.molecularisation_results) {
      localStorage.setItem('analysis_mol_results', JSON.stringify({
        session_id: savedSession.id,
        value_discipline: savedSession.value_discipline,
        roles: savedSession.molecularisation_results,
      }));
    }
    navigate(resumeTarget(savedSession.status));
  };

  // ── Render ───────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: 'var(--fs)', color: T.text, position: 'relative', zIndex: 2 }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '64px 40px 120px' }}>
        {/* Resume banner */}
        {!loadingSession && savedSession && (
          <div style={{
            background: `${T.teal}0A`,
            border: `1px solid ${T.teal}30`,
            borderRadius: T.radius,
            padding: '16px 20px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 2 }}>
                Resuming your saved analysis
              </p>
              <p style={{ fontSize: 13, color: T.textMuted }}>
                {savedSession.function} · {savedSession.roles?.length || 0} roles · {new Date(savedSession.updated_at || savedSession.created_at).toLocaleDateString()}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleResume}
                style={{
                  padding: '8px 16px', fontSize: 13, fontWeight: 600,
                  background: T.teal, color: '#fff', border: 'none',
                  borderRadius: 8, cursor: 'pointer',
                }}
              >
                Continue where you left off
              </button>
              <button
                onClick={handleStartFresh}
                style={{
                  padding: '8px 16px', fontSize: 13, fontWeight: 500,
                  background: 'transparent', color: T.textMuted,
                  border: `1px solid ${T.cardBorder}`, borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Start fresh
              </button>
            </div>
          </div>
        )}

        {/* Stepper (recipe 3.13) */}
        <div className="stepper" style={{ marginBottom: 16 }}>
          <div className="stepper-top">
            <span className="stepper-label">Step 1 of 4</span>
            <button className="stepper-back" onClick={() => {
              const slug = localStorage.getItem('demo_slug');
              navigate(slug ? `/demo/${slug}` : '/signal-room');
            }}>← Back</button>
          </div>
          <div className="stepper-rail">
            <div className="stepper-fill" style={{ width: '25%' }} />
          </div>
        </div>

        <HintBar screenId="analysis-upload" hints={HINTS} />

        {/* Headline */}
        <h1 className="h1" style={{ margin: '8px 0 8px', fontSize: 36 }}>
          Add roles to analyze
        </h1>
        <p style={{ fontSize: 15, color: 'var(--t3)', lineHeight: 1.6, marginBottom: 32 }}>
          Start by uploading JD PDFs or list the titles. We'll analyse transformation potential for each role.
        </p>

        {/* Inline messages */}
        {inlineMsg && <InlineMessage {...inlineMsg} onDismiss={() => setInlineMsg(null)} />}

        {/* Tab toggle */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--line)' }}>
          {[
            { key: 'upload', label: 'Upload JDs' },
            { key: 'titles', label: 'Enter Titles' },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`ct${activeTab === tab.key ? ' on' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── MODE A: Upload JDs ────────────────────── */}
        {activeTab === 'upload' && (
          <div>
            {/* Restored JD note */}
            {savedSession && uploadedFiles.filter(f => f.confirmed).length > 0 && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: `${T.teal}08`, border: `1px solid ${T.teal}20`, marginBottom: 16, fontSize: 13, color: T.teal }}>
                {uploadedFiles.filter(f => f.confirmed).length} JD{uploadedFiles.filter(f => f.confirmed).length !== 1 ? 's' : ''} already uploaded and analysed
              </div>
            )}
            {/* Drop zone (recipe 3.13) */}
            <div className="glass" style={{ padding: 0, marginBottom: 24 }}>
              <div
                className={`dropzone${dragOver ? ' drag-over' : ''}`}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <svg className="dropzone-icon" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 4v12m0-12l-4 4m4-4l4 4M4 20h16" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {uploading ? (
                  <div className="dropzone-title" style={{ color: 'var(--teal)' }}>
                    Processing files… this may take 30–60 seconds
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="dropzone-title">Drop a Job Description</div>
                      <div className="dropzone-copy">PDF · DOCX · TXT · paste · up to {maxRoles} files at once</div>
                    </div>
                    <div className="dropzone-hint">Or browse</div>
                  </>
                )}
              </div>
            </div>

            {/* Uploaded file cards */}
            {uploadedFiles.map((file, idx) => (
              <div
                key={idx}
                className="glass-light"
                style={{
                  border: file.confirmed ? '1px solid var(--teal)' : undefined,
                  borderRadius: T.radius,
                  padding: 16,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      {file.confirmed && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill={T.teal}>
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke={T.teal} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      {file.editingTitle ? (
                        <input
                          autoFocus
                          value={file.extracted_title}
                          onChange={(e) => updateUploadedTitle(idx, e.target.value)}
                          onBlur={() => setUploadedFiles((prev) => prev.map((f, i) => (i === idx ? { ...f, editingTitle: false } : f)))}
                          onKeyDown={(e) => e.key === 'Enter' && confirmUploadedFile(idx)}
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            border: `1px solid ${T.teal}`,
                            borderRadius: 6,
                            padding: '4px 8px',
                            outline: 'none',
                            width: '100%',
                          }}
                        />
                      ) : (
                        <span
                          style={{ fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
                          onClick={() => setUploadedFiles((prev) => prev.map((f, i) => (i === idx ? { ...f, editingTitle: true } : f)))}
                          title="Click to edit"
                        >
                          {file.extracted_title}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 12, color: T.textMuted }}>{file.filename}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ConfidenceBadge value={file.confidence} />
                    {!file.confirmed && (
                      <button
                        onClick={() => confirmUploadedFile(idx)}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: T.teal,
                          background: `${T.teal}12`,
                          border: `1px solid ${T.teal}40`,
                          borderRadius: 6,
                          padding: '4px 12px',
                          cursor: 'pointer',
                        }}
                      >
                        Confirm
                      </button>
                    )}
                    <button
                      onClick={() => removeUploadedFile(idx)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontSize: 16, padding: '0 4px' }}
                      title="Remove"
                    >
                      &times;
                    </button>
                  </div>
                </div>
                {file.responsibilities?.length > 0 && (
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
                    {file.responsibilities.slice(0, 4).map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                    {file.responsibilities.length > 4 && (
                      <li style={{ fontStyle: 'italic' }}>+{file.responsibilities.length - 4} more</li>
                    )}
                  </ul>
                )}
                {/* Filled / Vacant toggle */}
                {file.confirmed && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, color: file.filled !== false ? T.text : T.textMuted }}>
                      <input type="radio" name={`filled-upload-${idx}`} checked={file.filled !== false}
                        onChange={() => setUploadedFiles((prev) => prev.map((f, i) => i === idx ? { ...f, filled: true } : f))}
                        style={{ accentColor: T.teal }} />
                      Filled
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, color: file.filled === false ? T.teal : T.textMuted }}>
                      <input type="radio" name={`filled-upload-${idx}`} checked={file.filled === false}
                        onChange={() => setUploadedFiles((prev) => prev.map((f, i) => i === idx ? { ...f, filled: false } : f))}
                        style={{ accentColor: T.teal }} />
                      Vacant
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── MODE B: Enter Titles ──────────────────── */}
        {activeTab === 'titles' && (
          <div>
            {/* Input row (recipe 3.13 .title-row / .title-input) */}
            <div className="glass" style={{ padding: '16px 20px', marginBottom: 24 }}>
              <div className="title-row">
                <input
                  className="title-input"
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTitle()}
                  placeholder="e.g. CFO, Head of FP&A, Software Engineer…"
                />
                <button
                  className="btn-teal"
                  onClick={handleAddTitle}
                  disabled={lookingUp || !titleInput.trim()}
                  style={{
                    padding: '8px 18px', fontSize: 13, borderRadius: 8,
                    opacity: lookingUp || !titleInput.trim() ? 0.5 : 1,
                    cursor: lookingUp ? 'wait' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lookingUp ? 'Looking up…' : 'Add'}
                </button>
              </div>
            </div>

            {/* Title entries */}
            {titleEntries.map((entry, idx) => (
              <div
                key={idx}
                className="glass-light"
                style={{
                  border: entry.confirmed ? '1px solid var(--teal)' : undefined,
                  borderRadius: T.radius,
                  padding: 16,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {entry.confirmed && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke={T.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{entry.title}</span>
                    {entry.confirmed && entry.onet_title && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 99,
                          background: `${T.teal}15`,
                          color: T.teal,
                        }}
                      >
                        OCF matched: {entry.onet_title}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeTitleEntry(idx)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontSize: 16, padding: '0 4px' }}
                  >
                    &times;
                  </button>
                </div>

                {/* Match selection dropdown */}
                {entry.choosing && entry.matches?.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>
                      {entry.isFallback
                        ? `We couldn't find an exact match for "${entry.title}". Select the closest role:`
                        : 'Select the closest OCF match:'}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {entry.matches.map((m, mi) => (
                        <button
                          key={mi}
                          onClick={() => selectMatch(idx, m)}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            fontSize: 13,
                            border: `1px solid ${T.cardBorder}`,
                            borderRadius: 8,
                            background: T.cardBg,
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'border-color 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.borderColor = T.teal)}
                          onMouseLeave={(e) => (e.currentTarget.style.borderColor = T.cardBorder)}
                        >
                          <div>
                            <span style={{ fontWeight: 500 }}>{m.onet_title}</span>
                            <span style={{ color: T.textMuted, marginLeft: 8, fontSize: 11 }}>{m.onet_code}</span>
                          </div>
                          <span style={{ fontSize: 12, color: T.textMuted }}>{m.task_count} tasks &middot; {Math.round(m.similarity_score * 100)}% match</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {entry.choosing && (!entry.matches || entry.matches.length === 0) && (
                  <p style={{ fontSize: 13, color: '#dc2626', marginTop: 8 }}>No matches found. Try a different title.</p>
                )}

                {/* Filled / Vacant toggle */}
                {entry.confirmed && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, color: entry.filled !== false ? T.text : T.textMuted }}>
                      <input type="radio" name={`filled-title-${idx}`} checked={entry.filled !== false}
                        onChange={() => setTitleEntries((prev) => prev.map((t, i) => i === idx ? { ...t, filled: true } : t))}
                        style={{ accentColor: T.teal }} />
                      Filled
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, color: entry.filled === false ? T.teal : T.textMuted }}>
                      <input type="radio" name={`filled-title-${idx}`} checked={entry.filled === false}
                        onChange={() => setTitleEntries((prev) => prev.map((t, i) => i === idx ? { ...t, filled: false } : t))}
                        style={{ accentColor: T.teal }} />
                      Vacant
                    </label>
                  </div>
                )}
              </div>
            ))}

            {/* Running count */}
            {titleEntries.length > 0 && (
              <p style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>
                {titleEntries.filter((t) => t.confirmed).length} confirmed (min {minRoles} role{minRoles === 1 ? '' : 's'})
              </p>
            )}
          </div>
        )}

        {/* ── Confirmed roles summary (cross-tab) ──── */}
        {confirmedRoles.length > 0 && (
          <div style={{ marginTop: 24, padding: '16px 0', borderTop: `1px solid ${T.cardBorder}` }}>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>
              Confirmed roles ({confirmedRoles.length})
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {confirmedRoles.map((r, i) => (
                <span key={i} style={{
                  padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500,
                  background: `${T.teal}10`, border: `1px solid ${T.teal}30`, color: T.teal,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke={T.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {r.original_title || r.title}
                  <span style={{ fontSize: 10, color: T.textMuted }}>{r.source === 'jd_upload' ? 'JD' : 'Title'}</span>
                  <span style={{ fontSize: 10, color: r.filled ? T.textMuted : T.teal }}>{r.filled ? 'Filled' : 'Vacant'}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Bottom section ────────────────────────── */}
        <div
          style={{
            marginTop: 48,
            paddingTop: 24,
            borderTop: '1px solid var(--line)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div className="eyebrow">
            {roleCount < minRoles ? (
              <span>Need {minRoles - roleCount} more role{minRoles - roleCount !== 1 ? 's' : ''}</span>
            ) : (
              <span style={{ color: 'var(--teal)' }}>{roleCount} roles ready</span>
            )}
          </div>
          <button
            className="btn-teal"
            onClick={handleContinue}
            disabled={!canContinue || saving}
            style={{
              opacity: canContinue ? (saving ? 0.6 : 1) : 0.4,
              cursor: canContinue ? (saving ? 'wait' : 'pointer') : 'not-allowed',
              pointerEvents: canContinue ? 'auto' : 'none',
            }}
          >
            {saving ? 'Saving…' : 'Continue to Value Discipline →'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AnalysisWithExplainer = (props) => (<><Analysis {...props} /><DemoExplainer screen="ANALYSIS_UPLOAD" /></>);
export default AnalysisWithExplainer;
